import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, useLocation, } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from 'framer-motion';
import { useMediaQuery } from "@mui/material";
import resized from './assets/resized.webp';
import Loading from "./components/Pages/Loading";
import {
    mainVariant,
    aboutVariant,
    contactVariant,
    socials,
    menuNav,
    routes,
    projects,
} from './utils/data';
import "./App.css";

const Main = React.lazy(() => import("./components/Pages/Main"));
const About = React.lazy(() => import("./components/Pages/About"));
const Contact = React.lazy(() => import("./components/Pages/Contact"));
const Layout = React.lazy(() => import('./components/Pages/Portfolio/Layout'));
const Popup = React.lazy(() => import('./components/Pages/Portfolio/Popup'));

function App() {
    const [tooltip, setTooltip] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const isStaticRoutes = projects.map((item) => '/Portfolio/' + item.route);
    const [prevPath, setPrevPath] = useState(null);
    const [isStatic, setIsStatic] = useState(false);
    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(location.pathname.includes("/Portfolio") ? "(max-width: 1560px)" : "(max-width: 1400px)");
    const currentIndex = routes.indexOf(location.pathname);

    const handlePrevious = () => {
        if (currentIndex === 0) {
            navigate("/Contact");
        } else {
            navigate(routes[currentIndex - 1]);
        }
    };
    const handleNext = () => {
        if (currentIndex === routes.length - 1) {
            navigate("/");
        } else {
            navigate(routes[currentIndex + 1]);
        }
    };
    useEffect(() => {
        const currentPath = location.pathname;
        if (isStaticRoutes.includes(currentPath) || (prevPath === "/Portfolio" && isStaticRoutes.includes(currentPath)) || (isStaticRoutes.includes(prevPath) && currentPath === "/Portfolio")) {
            setIsStatic(true);
        } else {
            setIsStatic(false);
        }

        setPrevPath(currentPath);
    }, [location.pathname, isStaticRoutes, prevPath]);

    useEffect(() => {
        if (socials.some(({ link }) => location.pathname === link)) {
            setIsOpen(false);
        }
    }, [location.pathname,]);
    return (
        <div className={isMobile ? "mobile" : "App"}>
            <div className={isMobile ? "menu__navigation menu__navigation-res" : "menu__navigation"}>
                {menuNav.map(({ Icon, label, link }, index) => (<div
                    key={index}
                    style={{ position: "relative", display: "inline-block" }}
                    onMouseEnter={() => setTooltip(label)}
                    onMouseLeave={() => setTooltip("")}
                >
                    <Link
                        className="menu__navigation-link"
                        to={link}
                        title={label}
                        aria-label={label}
                    >
                        <Icon size={25} aria-hidden="true" />
                    </Link>
                    {tooltip === label && (
                        <div className="menu__navigation-linkLabel">
                            {label}
                        </div>
                    )}
                </div>
                ))}
            </div>
            <div className={isMobile ? 'arrow__navContainer arrow__navContainer-res' : 'arrow__navContainer'}>
                <div className="arrow__nav">
                    <ChevronLeft
                        size={25}
                        className="arrow"
                        onClick={handlePrevious}
                        aria-label="Previous Page"
                        role="button"
                    />
                    <ChevronRight
                        size={25}
                        className='arrow'
                        onClick={handleNext}
                        aria-label="Next Page"
                        role="button"
                    />
                </div>
            </div>
            <div className={isMobile ? 'leftPanel__container leftPanel__container-res' : 'leftPanel__container'}>
                <div
                    className={isMobile ? "leftPanel leftPanel__desktop" : "leftPanel"}
                >
                    <div className="leftPanel__avatar">
                        <img
                            src={resized}
                            alt="Profile"
                            className="leftPanel__avatar-image"
                            fetchPriority="high"
                            width="151"
                            height="151"
                        />
                    </div>
                    <h1 className={isMobile ? "leftPanel__name" : "leftPanel__nameMob"}>
                        Davor Koteski
                    </h1>
                    <p className="leftPanel__role">Front-End Engineer</p>
                    <div className="leftPanel__social-links">
                        {socials.map(social => {
                            const isInternalContact = social.link === '/Contact';
                            return isInternalContact ? (
                                <Link
                                    key={social.link}
                                    to={social.link}
                                    className="leftPanel__social-link"
                                    aria-label={social.ariaLabel}
                                >
                                    {social.icon}
                                </Link>
                            ) : (

                                <a
                                    key={social.link}
                                    href={social.link}
                                    className="leftPanel__social-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.ariaLabel}
                                >
                                    {social.icon}
                                </a>
                            );
                        })}
                    </div>
                    <a href="/resume/resume.pdf" download="resume.pdf">
                        <button className="leftPanel__download">Download CV</button>
                    </a>
                </div>
                <div className={isMobile ? "rightPanel rightPanel__res" : 'rightPanel'}>
                    <AnimatePresence mode='wait'  >
                        <Routes location={location} key={location.pathname}>
                            <Route path="/*"
                                element={
                                    isMobile ? (
                                        <div className="page-container">
                                            <Main isMobile={isMobile} />
                                        </div>
                                    ) : (
                                        <Suspense fallback={<Loading />}>
                                            <motion.div initial="initial" animate="animate" exit="exit" variants={mainVariant} className="page-container">
                                                <Main isMobile={isMobile} />
                                            </motion.div>
                                        </Suspense>
                                    )
                                }
                            />
                            <Route path="/About" element={
                                <Suspense fallback={<Loading />}>
                                    {isMobile ? (
                                        <div className="page-container">
                                            <About isMobile={isMobile} />
                                        </div>
                                    ) : (
                                        <motion.div initial="initial" animate="animate" exit="exit" variants={aboutVariant} className="page-container">
                                            <About isMobile={isMobile} />
                                        </motion.div>
                                    )}
                                </Suspense>
                            } />
                            <Route path='/Portfolio' element={
                                isMobile ? (
                                    <Layout className="page-container" location={location} isStatic={isStatic} />
                                ) : (
                                    <Suspense fallback={<Loading />}>
                                        <Layout className="page-container" location={location} isStatic={isStatic} />
                                    </Suspense>
                                )

                            } >
                                {projects.map((project, index) => (
                                    <Route
                                        key={index}
                                        path={project.route}
                                        element={<Popup isMobile={isMobile} project={project} navigate={navigate} location={location} />}
                                    />
                                ))}
                            </Route>
                            <Route path="/Contact" element={
                                isMobile ? (
                                    <div className="page-container">
                                        <Contact isMobile={isMobile} />
                                    </div>
                                ) : (
                                    <Suspense fallback={<Loading />}>
                                        <motion.div initial="initial" animate="animate" exit="exit" variants={contactVariant} className="page-container" >
                                            <Contact isMobile={isMobile} />
                                        </motion.div>
                                    </Suspense>
                                )
                            } />
                        </Routes>
                    </AnimatePresence>
                    {isMobile && (<button
                        className={`burger-menu ${isOpen ? "open" : ""}`}
                        onClick={toggleMenu}
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                    >
                        <div className="burger-bar top" aria-hidden="true"></div>
                        <div className="burger-bar middle" aria-hidden="true"></div>
                        <div className="burger-bar bottom" aria-hidden="true"></div>
                    </button>)}
                    {isMobile && (
                        <>
                            <div
                                className={`leftPanel__res-backdrop${isOpen ? " visible" : ""}`}
                                onClick={() => setIsOpen(false)}
                                aria-hidden="true"
                            />
                            <div className={isOpen ? "leftPanel__res leftPanel__res-open" : "leftPanel__res"}>
                                <div className="leftPanel__res-imageWrapper">
                                    <img
                                        src={resized}
                                        alt="Profile__mobile"
                                        className="leftPanel__res-image"
                                    />
                                </div>
                                <div className="leftPanel__res-info">
                                    <h2 className="leftPanel__res-name">Davor Koteski</h2>
                                    <span className="leftPanel__res-desc">Front-End Engineer</span>
                                </div>
                                <hr className="leftPanel__res-divider" />
                                <div className="leftPanel__res-menu">
                                    {menuNav.map(({ label, link }) => (
                                        <Link key={label} to={link} onClick={() => setIsOpen(false)} className="leftPanel__res-menuItem">
                                            {label}
                                        </Link>))}
                                </div>
                                <hr className="leftPanel__res-divider" />
                                <div className="leftPanel__res-contact">
                                    {socials.map(({ label, link, icon, ariaLabel }) => {
                                        const isContact = link === '/Contact';
                                        return isContact ? (
                                            <Link
                                                key={label}
                                                to={link}
                                                onClick={() => setIsOpen(false)}
                                                className="leftPanel__res-contactLink"
                                                aria-label={ariaLabel}
                                            >
                                                {icon}
                                            </Link>
                                        ) : (
                                            <a
                                                key={label}
                                                href={link}
                                                className="leftPanel__res-contactLink"
                                                aria-label={ariaLabel}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => setIsOpen(false)}
                                            >
                                                {icon}
                                            </a>
                                        )
                                    })}
                                </div>
                                <div className="leftPanel__res-wrapper">
                                    <a
                                        className="leftPanel__download"
                                        href="/resume/resume.pdf"
                                        download="resume.pdf"
                                    >
                                        Download CV
                                    </a>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default App;
